/**
 * Manifest diff view — shows changes between two manifest snapshots across
 * all entity types. Used by ReviewView's sandbox-review flow to preview
 * what a sandbox's proposed ops would change against the current manifest
 * (see ReviewView.tsx). Defaults to diffing against an empty baseline when
 * no `baselineManifest` is given.
 *
 * @see docs/impl/147-brisarx-atlas-design.html §9 Phase 4
 */
import { useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { computeManifestDiff, diffObject, type PropertyChange } from '@atlas-manifest/diff';
import type { DiffEntry } from '@atlas-manifest/diff';
import { Icon } from '../components/Icon.js';

export interface DiffViewProps {
  manifest: AtlasManifest;
  /** Compare against this manifest instead of an empty baseline. */
  baselineManifest?: AtlasManifest;
  /** Override the default "Changes from empty baseline" subtitle. */
  subtitle?: string;
}

function createEmptyManifest(): AtlasManifest {
  return {
    generatedAt: '2000-01-01T00:00:00Z',
    generatedFromCommit: 'empty',
    schemaVersion: 1,
    stats: {
      entityCount: 0,
      domainCount: 0,
      surfaceCount: 0,
      referenceCount: 0,
      hubCount: 0,
      unlinkedFeatureIds: [],
    },
    domains: [],
    entities: [],
    references: [],
    hubs: [],
    surfaces: [],
    cloudFunctions: [],
    events: [],
    paths: [],
    tags: [],
    stateMachines: [],
    flagCatalogRows: [],
  };
}

interface DiffSectionProps<T> {
  title: string;
  entries: DiffEntry<T>[];
}

function DiffSection<T>({ title, entries }: DiffSectionProps<T>): React.JSX.Element | null {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (entries.length === 0) return null;
  return (
    <section style={{ marginBottom: 'var(--sp-lg)' }}>
      <div className="section-label">{title} · {entries.length}</div>
      {entries.map((entry) => (
        <div key={entry.id}>
          <div
            className="ref-row"
            style={{ cursor: 'pointer' }}
            onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
          >
            <Icon
              name={entry.op === 'added' ? 'add_circle' : entry.op === 'removed' ? 'remove_circle' : 'edit'}
              size="sm"
              color={entry.op === 'added' ? 'var(--good)' : entry.op === 'removed' ? 'var(--danger)' : 'var(--caution)'}
            />
            <span className="mono">{entry.id}</span>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', marginLeft: 'auto' }}>{entry.op}</span>
          </div>
          {expandedId === entry.id && entry.op === 'changed' && entry.before && entry.after && (
            <div style={{ paddingLeft: 'var(--sp-lg)', paddingBottom: 'var(--sp-sm)' }}>
              {diffObject(entry.before as Record<string, unknown>, entry.after as Record<string, unknown>).map((c: PropertyChange) => (
                <div key={c.field} style={{ fontSize: 'var(--fs-xs)' }}>
                  <strong>{c.field}:</strong> {JSON.stringify(c.from)} → {JSON.stringify(c.to)}
                </div>
              ))}
            </div>
          )}
          {expandedId === entry.id && entry.op === 'added' && entry.after && (
            <div style={{ paddingLeft: 'var(--sp-lg)', paddingBottom: 'var(--sp-sm)' }}>
              {Object.entries(entry.after as Record<string, unknown>).map(([field, value]) => (
                <div key={field} style={{ fontSize: 'var(--fs-xs)' }}>
                  <strong>{field}:</strong> {JSON.stringify(value)}
                </div>
              ))}
            </div>
          )}
          {expandedId === entry.id && entry.op === 'removed' && entry.before && (
            <div style={{ paddingLeft: 'var(--sp-lg)', paddingBottom: 'var(--sp-sm)' }}>
              {Object.entries(entry.before as Record<string, unknown>).map(([field, value]) => (
                <div key={field} style={{ fontSize: 'var(--fs-xs)' }}>
                  <strong>{field}:</strong> {JSON.stringify(value)}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

export function DiffView({ manifest, baselineManifest, subtitle }: DiffViewProps): React.JSX.Element {
  const baseline = baselineManifest ?? createEmptyManifest();
  const diff = computeManifestDiff(baseline, manifest);

  return (
    <article>
      <div className="entity-head">
        <Icon name="difference" size="lg" label="Manifest changes" />
        <h1 className="entity-title">Manifest changes</h1>
      </div>

      <p className="entity-subtitle" style={{ marginBottom: 'var(--sp-lg)' }}>
        {subtitle ?? 'Changes from empty baseline'}
      </p>

      <div className="card" style={{ marginBottom: 'var(--sp-lg)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-md)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--good)', fontSize: '2em', fontWeight: 'bold' }}>
            {diff.added}
          </div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text3)' }}>Added</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--danger)', fontSize: '2em', fontWeight: 'bold' }}>
            {diff.removed}
          </div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text3)' }}>Removed</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--caution)', fontSize: '2em', fontWeight: 'bold' }}>
            {diff.changed}
          </div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text3)' }}>Changed</div>
        </div>
      </div>

      {diff.total === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--sp-lg)', color: 'var(--text3)' }}>
          <Icon name="check_circle" size="md" color="var(--good)" />
          <p>No changes{baselineManifest ? '' : ' — empty manifest'}</p>
        </div>
      ) : (
        <>
          <DiffSection title="Domains" entries={diff.domains} />
          <DiffSection title="Entities" entries={diff.entities} />
          <DiffSection title="References" entries={diff.references} />
          <DiffSection title="Surfaces" entries={diff.surfaces} />
          <DiffSection title="Events" entries={diff.events} />
          <DiffSection title="Cloud Functions" entries={diff.cloudFunctions} />
          <DiffSection title="State Machines" entries={diff.stateMachines} />
          <DiffSection title="Paths" entries={diff.paths} />
          <DiffSection title="Tags" entries={diff.tags} />
        </>
      )}
    </article>
  );
}
